import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const cancelled = location.pathname.includes("cancel");
  const sessionId = params.get("session_id");
  // checking | paid | failed | cancelled | pending (timed out, charge not confirmed either way) | missing (no session id on a success route)
  const initialState = cancelled ? "cancelled" : (!sessionId ? "missing" : "checking");
  const [state, setState] = useState(initialState);
  const [info, setInfo] = useState(null);
  const [rechecking, setRechecking] = useState(false);
  const aliveRef = useRef(true);

  // Support reference derived from the session id, so a pending user can quote it.
  const supportRef = sessionId
    ? `PNR-${String(sessionId).replace(/[^a-zA-Z0-9]/g, "").slice(-6).toUpperCase()}`
    : null;

  // One confirmation pass. Returns true once a terminal state (paid/failed) was reached,
  // false if it exhausted its attempts without a definitive answer (-> pending, NOT failed).
  const runConfirmation = useCallback(async () => {
    let tries = 0;
    const attempt = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!aliveRef.current) return true;
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("paid");
          await refresh();
          return true;
        }
        // Only a CONFIRMED decline/expiry is a real failure. A timeout is NOT.
        if (["failed", "expired", "canceled", "cancelled"].includes(data.payment_status)) {
          setState("failed");
          return true;
        }
      } catch (e) {
        /* transient — keep trying */
      }
      if (!aliveRef.current) return true;
      if (tries >= 8) return false; // exhausted without confirmation
      await new Promise((r) => setTimeout(r, 2000));
      return attempt();
    };
    return attempt();
  }, [sessionId, refresh]);

  useEffect(() => {
    aliveRef.current = true;
    if (cancelled || !sessionId) return;
    (async () => {
      const resolved = await runConfirmation();
      if (!aliveRef.current) return;
      // Timed out with no paid/failed answer -> we CANNOT claim "no charge". Show pending.
      if (!resolved) setState("pending");
    })();
    return () => { aliveRef.current = false; };
  }, [sessionId, cancelled, runConfirmation]);

  // Manual "Recheck status" from the pending state.
  const recheck = useCallback(async () => {
    if (!sessionId || rechecking) return;
    setRechecking(true);
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (!aliveRef.current) return;
      if (data.payment_status === "paid") {
        setInfo(data);
        setState("paid");
        await refresh();
      } else if (["failed", "expired", "canceled", "cancelled"].includes(data.payment_status)) {
        setState("failed");
      }
      // else: still not confirmed — stay in pending.
    } catch (e) {
      /* stay pending; the message already tells them to check their wallet */
    } finally {
      if (aliveRef.current) setRechecking(false);
    }
  }, [sessionId, refresh, rechecking]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-1 px-4 text-white">
      <div className="surface-premium w-full max-w-md rounded-2xl p-8 text-center shadow-[inset_0_1px_0_hsl(var(--edge-light)),inset_0_-1px_0_hsl(var(--edge-dark)),var(--elev-4)]" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Icon name="refresh" size={48} className="mx-auto animate-spin text-[#00D632]" />
            <h1 className="mt-5 font-display text-2xl font-black tracking-tight">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-meta">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <Icon name="check-circle" size={48} className="mx-auto text-[#00D632]" data-testid="payment-success-icon" />
            <h1 className="mt-5 font-display text-2xl font-black tracking-tight">Purchase complete!</h1>
            <p className="mt-2 text-sm text-meta">
              {info?.package} added — <b className="tabular-nums text-[#00D632]">{Number(info?.coins || 0).toLocaleString()} PCoins</b>
              {info?.cash ? <> + <b className="tabular-nums text-white">${Number(info.cash).toFixed(2)} PCash</b></> : null}.
            </p>
            {info?.first_purchase && (info?.bonus_coins > 0 || info?.bonus_cash > 0) && (
              <div data-testid="first-purchase-bonus" className="mt-3 rounded-xl border border-[#00D632]/40 bg-[#00D632]/10 p-3">
                <p className="text-sm font-black text-[#00D632]">First-purchase bonus applied</p>
                <p className="text-xs text-white/80 tabular-nums">+{Number(info.bonus_coins || 0).toLocaleString()} bonus PCoins &amp; +${Number(info.bonus_cash || 0).toFixed(2)} bonus PCash</p>
              </div>
            )}
            {info?.promo_bonus > 0 && (
              <div data-testid="promo-bonus" className={`mt-3 rounded-xl border p-3 ${info?.promo_restrict === "live_casino" ? "border-surface-border bg-surface-5" : "border-[#00D632]/40 bg-[#00D632]/10"}`}>
                <p className={`text-sm font-black ${info?.promo_restrict === "live_casino" ? "text-white" : "text-[#00D632]"}`}>{info.promo_title || "Deposit match"} applied</p>
                <p className="text-xs text-white/80 tabular-nums">+${Number(info.promo_bonus || 0).toFixed(2)} bonus funds{info?.promo_restrict === "live_casino" ? " (Live Dealer games)" : ""}</p>
              </div>
            )}
            <Button data-testid="go-casino" onClick={() => navigate("/casino")} className="focus-ring mt-6 w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Start playing</Button>
            <Link to="/wallet" className="focus-ring mt-3 block text-sm text-meta underline underline-offset-2 hover:text-white">Back to wallet</Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Icon name="clock" size={48} className="mx-auto text-[#F5A623]" data-testid="payment-pending-icon" />
            <h1 className="mt-5 font-display text-2xl font-black tracking-tight">Still confirming your payment</h1>
            <p className="mt-2 text-sm text-meta">
              This is taking longer than usual. Your bank may still be processing — we haven't been able to confirm
              it yet. <b className="text-white">Don't pay again.</b> Check your wallet in a moment, or recheck below.
            </p>
            {supportRef && (
              <p className="mt-3 text-xs text-[#71717a]">Purchase reference · <span className="font-semibold tabular-nums text-meta">{supportRef}</span></p>
            )}
            <Button data-testid="payment-recheck" onClick={recheck} disabled={rechecking} className="focus-ring mt-6 w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829] disabled:opacity-70">
              {rechecking ? "Rechecking…" : "Recheck status"}
            </Button>
            <Link to="/wallet" className="focus-ring mt-3 block text-sm text-meta underline underline-offset-2 hover:text-white" data-testid="payment-pending-wallet">Check my wallet</Link>
          </>
        )}
        {state === "missing" && (
          <>
            <Icon name="alert-circle" size={48} className="mx-auto text-[#F5A623]" data-testid="payment-missing-icon" />
            <h1 className="mt-5 font-display text-2xl font-black tracking-tight">We couldn't read this receipt</h1>
            <p className="mt-2 text-sm text-meta">
              This confirmation link is missing its payment reference, so we can't look it up. If you just checked
              out, open your wallet to see your balance — <b className="text-white">don't pay again</b> until you've
              checked.
            </p>
            <Button data-testid="payment-missing-wallet" onClick={() => navigate("/wallet")} className="focus-ring mt-6 w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Check my wallet</Button>
            <Link to="/support" className="focus-ring mt-3 block text-sm text-meta underline underline-offset-2 hover:text-white">Contact support</Link>
          </>
        )}
        {(state === "failed" || state === "cancelled") && (
          <>
            <Icon name="x" size={48} className="mx-auto text-[#EF4444]" data-testid="payment-failed-icon" />
            <h1 className="mt-5 font-display text-2xl font-black tracking-tight">{state === "cancelled" ? "Payment cancelled" : "Payment not completed"}</h1>
            <p className="mt-2 text-sm text-meta">No charge was made. You can try again anytime.</p>
            <Button data-testid="back-wallet" onClick={() => navigate("/wallet")} className="focus-ring mt-6 w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Back to wallet</Button>
          </>
        )}
      </div>
    </div>
  );
}
